/**
 * QuickTest — the right-side panel of the Guardrails view.
 *
 * Renders for the rule the user has clicked in the list. The merchant
 * fills a sample mandate's intent fields, clicks Run test, and the
 * server runs the actual evaluator handler against the rule.
 *
 *   1. r03 / r04 / r13 (rate-limit) are not testable here — they consult
 *      an edge binding that doesn't exist in this server context. The
 *      panel surfaces the limitation instead of running.
 *   2. All other rules run through `policy.testRule(...)`. The server
 *      returns the evaluator's outcome (approved / rejected /
 *      escalated / evaluator_error / not_supported); the panel
 *      tone-codes the result.
 *
 * No simulation, no fake metrics — the rule's actual handler runs
 * against the actual sample mandate. Result accuracy ≡ wire accuracy.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, FlaskConical, HelpCircle, PlayCircle, ShieldCheck } from 'lucide-react';

import type { Rule, RuleType, SiteId } from '@sill/types';
import type { PolicyTestOutcome, PolicyTestSample } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { useC } from '../../lib/theme.js';
import { Card, Tooltip, type PillTone } from '../../components/atoms/index.js';
import { ruleByType } from './catalog.js';
import { friendlyReason, friendlyTestError } from './helpers.js';
import { fieldOrderFor, driversFor, type FieldKey } from './quicktest-fields.js';
import { presetsFor, type TestScenario } from './test-presets.js';

/**
 * Per-rule sample-field ordering.
 *
 * The form renders only the fields a rule lists (see `fieldOrderFor`), and
 * the field that actually *drives* the rule's outcome leads so cause-and-
 * effect is obvious. The definitions live in `quicktest-fields.ts` (no
 * React) so the `quicktest-fields-drift` test can assert that every
 * preset's driving field is surfaced. The preset populates that field with
 * the rule-relevant value; leading with it means the merchant sees what
 * changed when they click Pass / Fail.
 */
const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

function toneFg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeFg;
    case 'amber': return C.amberFg;
    case 'coral': return C.coralFg;
    case 'sky': return C.skyFg;
    case 'mint': return C.mintFg;
    case 'neutral':
    default:
      return C.muted;
  }
}

function toneBg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeDim;
    case 'amber': return C.amberDim;
    case 'coral': return C.coralDim;
    case 'sky': return C.skyDim;
    case 'mint': return C.mintDim;
    case 'neutral':
    default:
      return 'transparent';
  }
}

function outcomeTone(outcome: PolicyTestOutcome['outcome']): PillTone {
  switch (outcome) {
    case 'approved':
      return 'lime';
    case 'escalated':
      return 'amber';
    case 'rejected':
    case 'evaluator_error':
      return 'coral';
    case 'not_supported':
      return 'sky';
  }
}

function outcomeLabel(outcome: PolicyTestOutcome['outcome']): string {
  switch (outcome) {
    case 'approved': return 'APPROVED';
    case 'rejected': return 'REJECTED';
    case 'escalated': return 'ESCALATED';
    case 'evaluator_error': return 'EVALUATOR ERROR';
    case 'not_supported': return 'NOT SUPPORTED';
  }
}

const DEFAULT_SAMPLE: PolicyTestSample = {
  agent_id: 'agent_openai_chatgpt_user',
  validity_seconds: 60,
  intent: {
    action: 'browse_catalog',
    merchant: 'example-store',
    sku: 'SKU-001',
    max_amount: 0,
    currency: 'USD',
  },
  principal: { type: 'human', verification: 'delegated' },
};

export interface QuickTestProps {
  site_id: SiteId | undefined;
  rule: Rule | null;
}

export function QuickTest({ site_id, rule }: QuickTestProps): JSX.Element {
  const C = useC();
  const api = useApi();

  const [sample, setSample] = useState<PolicyTestSample>(DEFAULT_SAMPLE);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<PolicyTestOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeScenarioLabel, setActiveScenarioLabel] = useState<string | null>(null);
  // Whether the (inert-for-this-rule) mandate-context fields are expanded.
  const [showContext, setShowContext] = useState(false);

  const presets = useMemo<TestScenario[]>(
    () => (rule !== null ? presetsFor(rule) : []),
    [rule],
  );

  // When the user picks a different rule, reset everything AND pre-load
  // the rule's first preset (so the form's defaults are relevant, not
  // generic). Falls back to DEFAULT_SAMPLE when no presets exist.
  useEffect(() => {
    setOutcome(null);
    setError(null);
    setShowContext(false);
    if (presets.length > 0 && presets[0] !== undefined) {
      setSample(presets[0].sample);
      setActiveScenarioLabel(presets[0].label);
    } else {
      setSample(DEFAULT_SAMPLE);
      setActiveScenarioLabel(null);
    }
    // We intentionally key on rule_id so re-rendering the same rule
    // doesn't blow away the user's in-flight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.rule_id]);

  const catalog = useMemo(
    () => (rule !== null ? ruleByType(rule.type) : undefined),
    [rule],
  );
  // Rules that read runtime/edge-only context the origin QuickTest sandbox
  // does not provide, so they surface a "not testable here" notice instead
  // of running: r03/r04/r13 (CF rate-limit bindings), r12 (CF geo), and r17
  // (compares the signed principal.user_ref vs intent.target_user_ref under
  // the deployment key salt — neither is editable in this sample form yet).
  // r29 IS testable: its policy half reads only chain presence/depth/head,
  // which the server-side composer synthesizes (unsigned — signature
  // validity is the verifier's job, not this rule's).
  const needsEdgeContext =
    rule?.type === 'r03'
    || rule?.type === 'r04'
    || rule?.type === 'r13'
    || rule?.type === 'r12'
    || rule?.type === 'r17';
  const nonPolicyLayer =
    catalog?.enforcementLayer && catalog.enforcementLayer !== 'policy'
      ? catalog.enforcementLayer
      : null;
  const isDisabled = rule !== null && !rule.enabled;

  const loadScenario = (s: TestScenario): void => {
    setSample(s.sample);
    setActiveScenarioLabel(s.label);
    setOutcome(null);
    setError(null);
  };

  // Any direct edit to a sample field means the user has drifted from
  // the active preset — drop the highlight so it doesn't claim the
  // current sample reflects the preset.
  const editSample = (next: PolicyTestSample): void => {
    setSample(next);
    setActiveScenarioLabel(null);
  };

  if (rule === null) {
    return (
      <Card className="p-5">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 8,
          }}
        >
          QUICKTEST
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          Click a rule on the left to test it against a sample mandate.
          The server runs the actual evaluator handler — no fake
          results.
        </div>
      </Card>
    );
  }

  const runTest = async (): Promise<void> => {
    if (site_id === undefined) return;
    setRunning(true);
    setError(null);
    setOutcome(null);
    try {
      const r = await api.policy.testRule(site_id, rule, sample);
      setOutcome(r);
    } catch (e) {
      setError(friendlyTestError(e instanceof Error ? e.message : 'Test failed.'));
    } finally {
      setRunning(false);
    }
  };

  // Split the rule's fields into the ones its predicate READS (drivers — marked
  // + always shown) and the inert-for-this-rule context (collapsed), so the
  // merchant isn't editing inputs that can't move the result (review 2026-06-10).
  const fieldOrder = fieldOrderFor(rule.type);
  const drivers = driversFor(rule.type);
  const driverFields = drivers !== null ? fieldOrder.filter((fk) => drivers.has(fk)) : fieldOrder;
  const contextFields = drivers !== null ? fieldOrder.filter((fk) => !drivers.has(fk)) : [];

  // The loaded scenario (if any) + whether the run's outcome disagreed with what
  // the scenario expected — usually a sign the rule's on-match action is set the
  // wrong way (e.g. ALLOW where REJECT was intended).
  const activePreset =
    activeScenarioLabel !== null
      ? presets.find((s) => s.label === activeScenarioLabel)
      : undefined;
  // Classify a run vs the loaded scenario. A scenario's Fail/Edge case expects
  // the rule to STOP the mandate (reject or escalate are both "blocked/caught").
  //   - protectionGap: it expected a block but the mandate was APPROVED — the
  //     rule let it through (often an ALLOW action, or a real gap). Concerning.
  //   - routedDifferently: it WAS caught, just via a different action than the
  //     scenario assumed (e.g. escalate instead of reject). Valid — informational.
  const expectedBlock =
    activePreset !== undefined
    && (activePreset.expectedOutcome === 'rejected' || activePreset.expectedOutcome === 'escalated');
  const actualBlocked = outcome !== null && (outcome.outcome === 'rejected' || outcome.outcome === 'escalated');
  const protectionGap = outcome !== null && expectedBlock && outcome.outcome === 'approved';
  const routedDifferently =
    outcome !== null
    && activePreset !== undefined
    && expectedBlock
    && actualBlocked
    && outcome.outcome !== activePreset.expectedOutcome;
  const otherMismatch =
    outcome !== null
    && activePreset !== undefined
    && outcome.outcome !== activePreset.expectedOutcome
    && !protectionGap
    && !routedDifferently;

  const renderField = (fk: FieldKey): JSX.Element | null => {
    switch (fk) {
      case 'agent_id':
        return (
          <Field key={fk} C={C} label="Agent ID">
            <Input
              C={C}
              value={sample.agent_id ?? ''}
              onChange={(v) => editSample({ ...sample, agent_id: v })}
              placeholder="agent_openai_chatgpt_user"
            />
          </Field>
        );
      case 'action':
        return (
          <Field key={fk} C={C} label="Action">
            <Input
              C={C}
              value={sample.intent.action}
              onChange={(v) => editSample({ ...sample, intent: { ...sample.intent, action: v } })}
            />
          </Field>
        );
      case 'merchant':
        return (
          <Field key={fk} C={C} label="Merchant">
            <Input
              C={C}
              value={sample.intent.merchant}
              onChange={(v) => editSample({ ...sample, intent: { ...sample.intent, merchant: v } })}
            />
          </Field>
        );
      case 'sku':
        return (
          <Field key={fk} C={C} label="SKU">
            <Input
              C={C}
              value={sample.intent.sku}
              onChange={(v) => editSample({ ...sample, intent: { ...sample.intent, sku: v } })}
            />
          </Field>
        );
      case 'amount_currency':
        return (
          <div key={fk} className="grid grid-cols-2 gap-2">
            <Field C={C} label="Max amount">
              <Input
                C={C}
                type="number"
                value={String(sample.intent.max_amount)}
                onChange={(v) =>
                  editSample({ ...sample, intent: { ...sample.intent, max_amount: Number(v) || 0 } })
                }
              />
            </Field>
            <Field C={C} label="Currency">
              <Input
                C={C}
                value={sample.intent.currency}
                maxLength={3}
                onChange={(v) =>
                  editSample({
                    ...sample,
                    // Letters only, uppercased — a 3-letter ISO-4217-shaped code.
                    intent: { ...sample.intent, currency: v.toUpperCase().replace(/[^A-Z]/g, '') },
                  })
                }
              />
              {!/^[A-Z]{3}$/.test(sample.intent.currency) ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 10,
                    color: C.amberFg,
                    lineHeight: 1.4,
                    marginTop: 3,
                  }}
                >
                  Use a 3-letter code (e.g. USD, EUR). Anything else is treated as USD in the test.
                </div>
              ) : null}
            </Field>
          </div>
        );
      case 'validity_seconds':
        return (
          <Field key={fk} C={C} label="Validity (seconds)">
            <Input
              C={C}
              type="number"
              value={String(sample.validity_seconds ?? 60)}
              onChange={(v) => editSample({ ...sample, validity_seconds: Number(v) || 60 })}
            />
          </Field>
        );
      case 'cart_total':
        return (
          <Field key={fk} C={C} label="Cart total (AP2 cart signal)">
            <Input
              C={C}
              type="number"
              value={sample.intent.cart_total === undefined ? '' : String(sample.intent.cart_total)}
              placeholder="empty = no cart signal"
              onChange={(v) => {
                const intent = { ...sample.intent };
                if (v.trim() === '') delete intent.cart_total;
                else intent.cart_total = Number(v) || 0;
                editSample({ ...sample, intent });
              }}
            />
          </Field>
        );
      case 'cart_currency':
        return (
          <Field key={fk} C={C} label="Cart currency (AP2 cart signal)">
            <Input
              C={C}
              maxLength={3}
              value={sample.intent.cart_currency ?? ''}
              placeholder="empty = no cart signal"
              onChange={(v) => {
                const intent = { ...sample.intent };
                if (v.trim() === '') delete intent.cart_currency;
                else intent.cart_currency = v.toUpperCase();
                editSample({ ...sample, intent });
              }}
            />
          </Field>
        );
      case 'quoted_total':
        return (
          <Field key={fk} C={C} label="Quoted total (signed quote)">
            <Input
              C={C}
              type="number"
              value={sample.intent.quoted_total === undefined ? '' : String(sample.intent.quoted_total)}
              placeholder="empty = no quoted reference"
              onChange={(v) => {
                const intent = { ...sample.intent };
                if (v.trim() === '') delete intent.quoted_total;
                else intent.quoted_total = Number(v) || 0;
                editSample({ ...sample, intent });
              }}
            />
          </Field>
        );
      case 'skill_manifest_hash':
        return (
          <Field key={fk} C={C} label="Skill manifest digest (signed)">
            <Input
              C={C}
              value={sample.intent.skill_manifest_hash ?? ''}
              placeholder="empty = no digest (fail-safe reject)"
              onChange={(v) => {
                const intent = { ...sample.intent };
                const next = v.trim().toLowerCase();
                if (next === '') delete intent.skill_manifest_hash;
                else intent.skill_manifest_hash = next;
                editSample({ ...sample, intent });
              }}
            />
          </Field>
        );
      case 'delegation_chain': {
        const delegation = sample.delegation ?? { present: false };
        return (
          <div key={fk} className="grid grid-cols-2 gap-2">
            <Field C={C} label="Delegation chain">
              <Select
                C={C}
                value={delegation.present ? 'present' : 'none'}
                options={['none', 'present']}
                onChange={(v) =>
                  editSample({
                    ...sample,
                    delegation: { ...delegation, present: v === 'present' },
                  })
                }
              />
            </Field>
            <Field C={C} label="Chain head (first delegator)">
              <input
                value={delegation.head_agent_id ?? ''}
                disabled={!delegation.present}
                placeholder="agent_orchestrator_test"
                onChange={(e) =>
                  editSample({
                    ...sample,
                    delegation: {
                      ...delegation,
                      ...(e.target.value.trim().length > 0
                        ? { head_agent_id: e.target.value.trim() }
                        : {}),
                    },
                  })
                }
                className="w-full px-3 py-2"
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: C.text,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  outline: 'none',
                  opacity: delegation.present ? 1 : 0.5,
                }}
              />
            </Field>
          </div>
        );
      }
      case 'principal': {
        const principal = sample.principal ?? {
          type: 'human' as const,
          verification: 'delegated' as const,
        };
        return (
          <div key={fk} className="grid grid-cols-2 gap-2">
            <Field C={C} label="Principal type">
              <Select
                C={C}
                value={principal.type}
                options={['human', 'organization', 'none']}
                onChange={(v) =>
                  editSample({
                    ...sample,
                    principal: { ...principal, type: v as 'human' | 'organization' | 'none' },
                  })
                }
              />
            </Field>
            <Field C={C} label="Verification">
              <Select
                C={C}
                value={principal.verification}
                options={['delegated', 'direct', 'none']}
                onChange={(v) =>
                  editSample({
                    ...sample,
                    principal: { ...principal, verification: v as 'delegated' | 'direct' | 'none' },
                  })
                }
              />
            </Field>
          </div>
        );
      }
    }
  };

  return (
    <Card className="p-5 sticky top-24">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 4,
        }}
      >
        QUICKTEST
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 16,
          color: C.text,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {rule.display_name ?? catalog?.name ?? rule.rule_id}
      </div>
      {/* Category pill removed (UX review 2026-06-01) — the rule list on
          the left already shows the category icon + the filter tab, so
          repeating the category here is redundant. Keep the rule.type
          mono code: it's the audit-trail identity and useful here. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>
          {rule.type}
        </span>
      </div>

      {nonPolicyLayer ? (
        // ADR-0032 §4.1 — rules enforced OUTSIDE the policy evaluator
        // (verifier r11, webhook r27, response r21) have no policy-layer
        // handler the dashboard could exercise.
        <div
          className="p-3 flex items-start gap-2"
          style={{
            background: C.skyDim,
            border: `1px dashed ${C.skyFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={14} color={C.skyFg} className="flex-shrink-0 mt-0.5" />
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            {nonPolicyLayer === 'edge' ? (
              <>
                <strong>Not testable from dashboard — enforced at the edge ingress.</strong>{' '}
                This always-on guard rate-limits MCP session and tool-listing
                requests before any mandate exists, so the policy evaluator
                never sees them. It is enforced normally on live traffic;
                excess requests are shed at the edge.
              </>
            ) : nonPolicyLayer === 'verifier' ? (
              <>
                <strong>Not testable from dashboard — enforced at the verifier layer, before policy evaluation.</strong>{' '}
                Replayed mandates are rejected before policy evaluation. The
                rule is enforced normally on live traffic; see your audit log
                for replay rejections.
              </>
            ) : nonPolicyLayer === 'webhook' ? (
              <>
                <strong>Not testable from dashboard — enforced in the webhook handler.</strong>{' '}
                Inbound webhooks are HMAC-verified (dual test/live secret)
                before any handler runs. The rule is enforced normally on
                live traffic; failed signatures are rejected at the edge.
              </>
            ) : nonPolicyLayer === 'dashboard' ? (
              <>
                <strong>Not testable from dashboard — enforced in the dashboard refund flow.</strong>{' '}
                This rule gates on a human dashboard-user’s role (which an
                agent mandate never carries), not the inbound mandate. It is
                not an agent-policy handler.
              </>
            ) : nonPolicyLayer === 'origin' ? (
              <>
                <strong>Not testable from dashboard — enforced at the origin, at the charge.</strong>{' '}
                {rule.type === 'r31' ? (
                  <>
                    This rule is configured here but evaluated at the
                    rail-neutral charge seam against the organization’s
                    rolling settled-spend total across every settlement
                    rail (a Postgres aggregate), not by this evaluator. It
                    is enforced normally on live traffic; see your audit log
                    for cap rejections.
                  </>
                ) : (
                  <>
                    This rule is configured here but evaluated at the rail
                    executor against the user’s trailing-24h spend (a
                    Postgres aggregate), not by this evaluator. It is
                    enforced normally on live traffic; see your audit log
                    for cap rejections.
                  </>
                )}
              </>
            ) : (
              <>
                <strong>Not testable from dashboard — enforced at the response layer.</strong>{' '}
                This rule acts on the agent’s outbound response, not the
                inbound mandate the evaluator sees. It is not a policy
                handler.
              </>
            )}
          </div>
        </div>
      ) : needsEdgeContext ? (
        <div
          className="p-3 flex items-start gap-2"
          style={{
            background: C.skyDim,
            border: `1px dashed ${C.skyFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={14} color={C.skyFg} className="flex-shrink-0 mt-0.5" />
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            <strong>Not testable from the dashboard.</strong> This rule reads
            runtime context the test sandbox here doesn’t have — a Cloudflare
            rate-limit binding (per-agent / per-IP), the request’s resolved
            country (geofence), or the signed per-customer identifiers
            compared under the deployment key salt (data scoping). The rule
            is enforced normally on live traffic; see your audit log to
            confirm it fires.
          </div>
        </div>
      ) : isDisabled ? (
        <div
          className="p-3 flex items-start gap-2"
          style={{
            background: C.amberDim,
            border: `1px dashed ${C.amberFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={14} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            <strong>Rule is disabled.</strong> The evaluator skips disabled
            rules at publish time — every mandate would pass through this
            rule without it firing. Toggle ENABLED on the list to test it.
          </div>
        </div>
      ) : (
        <>
          {presets.length > 0 ? (
            <>
              <div
                className="flex items-center gap-1.5"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  marginBottom: 6,
                }}
              >
                SCENARIOS
                <Tooltip
                  content="PASS: a normal mandate the rule should let through. FAIL: the exact violation the rule exists to catch. EDGE: a boundary / corner case — usually something deliberately OUTSIDE the rule's scope (e.g. for the spend cap, a non-USD order the USD-only cap doesn't apply to), to confirm the rule doesn't over-reach. Each preset's description (below the buttons) says exactly what it checks."
                  side="top"
                  style={{ cursor: 'help' }}
                >
                  <HelpCircle size={11} color={C.muted} aria-label="Scenarios help" />
                </Tooltip>
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {presets.map((s) => {
                  const sel = activeScenarioLabel === s.label;
                  const tone: PillTone =
                    s.expectedOutcome === 'approved'
                      ? 'lime'
                      : s.expectedOutcome === 'rejected'
                        ? 'coral'
                        : s.expectedOutcome === 'escalated'
                          ? 'amber'
                          : 'sky';
                  return (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => loadScenario(s)}
                      className="px-3 py-1.5"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: 1,
                        color: sel ? toneFg(C, tone) : C.muted,
                        background: sel ? toneBg(C, tone) : 'transparent',
                        border: `1px solid ${sel ? toneFg(C, tone) : C.border}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      {s.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              {activeScenarioLabel !== null ? (() => {
                const active = presets.find((s) => s.label === activeScenarioLabel);
                if (!active) return null;
                const tone: PillTone =
                  active.expectedOutcome === 'approved'
                    ? 'lime'
                    : active.expectedOutcome === 'rejected'
                      ? 'coral'
                      : active.expectedOutcome === 'escalated'
                        ? 'amber'
                        : 'sky';
                return (
                  <div className="mb-4 flex items-start gap-2 flex-wrap">
                    <span
                      style={{
                        fontFamily: monoFont,
                        fontSize: 9,
                        letterSpacing: 1.5,
                        color: toneFg(C, tone),
                        background: toneBg(C, tone),
                        padding: '2px 6px',
                        borderRadius: 2,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      EXPECTED: {active.expectedOutcome.toUpperCase()}
                    </span>
                    <span
                      style={{
                        fontFamily: sansFont,
                        fontSize: 11,
                        color: C.muted,
                        lineHeight: 1.5,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {active.description}
                    </span>
                  </div>
                );
              })() : null}
            </>
          ) : null}

          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            SAMPLE MANDATE
          </div>

          <div className="space-y-3 mb-4">
            {drivers !== null ? (
              <>
                <div
                  className="flex items-center gap-1.5"
                  style={{ fontFamily: monoFont, fontSize: 9, color: C.limeFg, letterSpacing: 1.5 }}
                >
                  DRIVES {rule.type.toUpperCase()}
                  <Tooltip
                    content="The field(s) this rule's check actually reads — editing them changes the result. The mandate-context fields below are inert for this rule."
                    side="top"
                    style={{ cursor: 'help' }}
                  >
                    <HelpCircle size={11} color={C.muted} aria-label="Rule drivers help" />
                  </Tooltip>
                </div>
                {driverFields.map(renderField)}
                {contextFields.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowContext((s) => !s)}
                      style={{
                        fontFamily: monoFont,
                        fontSize: 9,
                        color: C.muted,
                        letterSpacing: 1,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        textAlign: 'left',
                      }}
                    >
                      {showContext ? '− Hide' : '+ Show'} mandate context ({contextFields.length}) — doesn’t affect {rule.type}
                    </button>
                    {showContext ? (
                      // Read-only: these fields don't feed this rule's check, so
                      // they're greyed + non-interactive (shown for context only).
                      <div
                        className="space-y-3"
                        style={{ opacity: 0.45, pointerEvents: 'none', userSelect: 'none' }}
                        aria-disabled="true"
                      >
                        {contextFields.map(renderField)}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : (
              fieldOrder.map(renderField)
            )}
          </div>

          <button
            type="button"
            onClick={() => void runTest()}
            disabled={running || site_id === undefined}
            className="flex items-center justify-center gap-2 w-full px-3 py-2 mb-4"
            style={{
              background: running ? C.surface2 : C.lime,
              color: running ? C.muted : C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              border: `1px solid ${running ? C.border : C.lime}`,
              cursor: running ? 'not-allowed' : 'pointer',
            }}
          >
            {running ? (
              <>
                <FlaskConical size={13} /> Running…
              </>
            ) : (
              <>
                <PlayCircle size={13} /> Run test
              </>
            )}
          </button>

          {error !== null ? (
            <div
              className="p-2 flex items-start gap-2 mb-4"
              style={{
                background: C.coralDim,
                border: `1px solid ${C.coralFg}`,
                borderRadius: 2,
              }}
            >
              <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{error}</span>
            </div>
          ) : null}

          {outcome !== null ? (
            <div
              className="p-3"
              style={{
                background: toneBg(C, outcomeTone(outcome.outcome)),
                border: `1px solid ${toneFg(C, outcomeTone(outcome.outcome))}`,
                borderRadius: 2,
              }}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <ShieldCheck size={13} color={toneFg(C, outcomeTone(outcome.outcome))} />
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1.5,
                    color: toneFg(C, outcomeTone(outcome.outcome)),
                    fontWeight: 600,
                  }}
                >
                  {outcomeLabel(outcome.outcome)}
                </span>
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    letterSpacing: 1,
                    color: C.muted,
                  }}
                >
                  · {outcome.action_taken}
                </span>
              </div>
              {friendlyReason(outcome.reason) !== undefined ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.5,
                  }}
                >
                  {friendlyReason(outcome.reason)}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Protection gap — the rule did NOT block (concerning). */}
          {protectionGap && activePreset !== undefined && outcome !== null ? (
            <div
              className="p-3 mt-3 flex items-start gap-2"
              style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
            >
              <AlertOctagon size={14} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>This mandate was approved — your rule didn’t block it.</strong> The{' '}
                {activeScenarioLabel} scenario expected it{' '}
                <span style={{ fontFamily: monoFont }}>
                  {activePreset.expectedOutcome.toUpperCase()}
                </span>
                .{' '}
                {rule.action_on_match === 'allow' ? (
                  <>
                    Your rule’s <em>If matches</em> action is <strong>ALLOW</strong> — when it
                    matches, it <em>approves</em> the mandate as an explicit exemption and skips the
                    remaining rules. To block this case, change the action to <strong>REJECT</strong>.
                  </>
                ) : (
                  <>
                    The rule didn’t fire for this sample — check its params (and order: an earlier{' '}
                    <em>allow</em> rule can approve before this one runs).
                  </>
                )}
              </div>
            </div>
          ) : routedDifferently && activePreset !== undefined && outcome !== null ? (
            // Caught, just via a different action than the scenario assumed — fine.
            <div
              className="p-3 mt-3 flex items-start gap-2"
              style={{ background: C.skyDim, border: `1px solid ${C.skyFg}`, borderRadius: 2 }}
            >
              <ShieldCheck size={14} color={C.skyFg} className="flex-shrink-0 mt-0.5" />
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>Caught — just routed differently.</strong> Your rule{' '}
                {outcome.outcome === 'escalated' ? 'ESCALATED (queued for human review)' : 'REJECTED'}{' '}
                this; the {activeScenarioLabel} scenario assumed{' '}
                <span style={{ fontFamily: monoFont }}>
                  {activePreset.expectedOutcome.toUpperCase()}
                </span>
                . The violation was still stopped — your <em>If matches</em> action just routes it
                to {outcome.outcome === 'escalated' ? 'review' : 'a hard reject'} instead. That’s a
                valid choice; no action needed.
              </div>
            </div>
          ) : otherMismatch && activePreset !== undefined && outcome !== null ? (
            <div
              className="p-3 mt-3 flex items-start gap-2"
              style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}
            >
              <AlertOctagon size={14} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>Result differs from the scenario.</strong> This came back{' '}
                <span style={{ fontFamily: monoFont }}>{outcomeLabel(outcome.outcome)}</span>; the{' '}
                {activeScenarioLabel} scenario expected{' '}
                <span style={{ fontFamily: monoFont }}>
                  {activePreset.expectedOutcome.toUpperCase()}
                </span>
                . Re-check the rule’s params and its <em>If matches</em> action.
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Small input atoms used inside the panel.
// ---------------------------------------------------------------------------

function Field({
  C,
  label,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          marginBottom: 4,
        }}
      >
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function Input({
  C,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
}: {
  C: ReturnType<typeof useC>;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
  maxLength?: number;
}): JSX.Element {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      {...(maxLength !== undefined ? { maxLength } : {})}
      {...(placeholder !== undefined ? { placeholder } : {})}
      className="w-full px-3 py-2"
      style={{
        fontFamily: monoFont,
        fontSize: 12,
        color: C.text,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        outline: 'none',
      }}
    />
  );
}

function Select({
  C,
  value,
  options,
  onChange,
}: {
  C: ReturnType<typeof useC>;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2"
      style={{
        fontFamily: monoFont,
        fontSize: 12,
        color: C.text,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        outline: 'none',
      }}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
